import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, UserPlus } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { canForceSlot } from '../../lib/roles';
import { listConceptos, freeSlots, createCita, getQuirofanoDia, strToDate, ofrecerConfirmacionWhatsApp, minutosConcepto, valorTipoCita, leerTipoCita, nombreReserva } from '../../lib/citasApi';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import CirujanoSelect from './CirujanoSelect';
import LugarQuirofanoSelect from './LugarQuirofanoSelect';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { PatientCombobox } from '../PatientCombobox';
import PatientFormDialog from '../PatientFormDialog';
import ConceptoOptions from './ConceptoOptions';
import InfoClinicaPaciente from './InfoClinicaPaciente';
import {
  AvisoProhibidoEnCita, DialogoPacienteProhibido, esErrorProhibido, useAvisoProhibido,
} from './AvisoPacienteProhibido';

/**
 * Crear cita desde la agenda con el hueco prefijado (click en la rejilla).
 *
 * Props:
 *  - open / onOpenChange
 *  - agenda:       agenda activa
 *  - defaultDate:  "YYYY-MM-DD" del hueco pulsado
 *  - defaultStart: "HH:MM" del hueco pulsado (editable vía Select de huecos libres)
 *  - onCreated:    callback tras crear con éxito
 */

function longDate(dateStr) {
  if (!dateStr) return '—';
  const s = strToDate(dateStr).toLocaleDateString('es-ES', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export default function CitaCreateDialog({ open, onOpenChange, agenda, defaultDate, defaultStart, onCreated }) {
  const { user } = useAuth();
  const admin = canForceSlot(user);

  const [conceptos, setConceptos] = useState([]);
  const [patients, setPatients] = useState([]);
  const [patientId, setPatientId] = useState('');
  const [conceptCode, setConceptCode] = useState('');
  // «Reserva <tipo>» (12/10/2026): el mismo tipo con la marca de reserva.
  const [reserva, setReserva] = useState(false);
  const [start, setStart] = useState(defaultStart || '');
  // Duración corregida a mano (string) o null = la del tipo de cita. Se
  // descarta al cambiar de tipo, que vuelve a rellenarla sola (2026-09-03,
  // Dirección: cada tipo de cirugía lleva su duración, editable).
  const [duracionManual, setDuracionManual] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  // Detalles de la cita: OBLIGATORIOS y siempre a la vista (2026-09-03,
  // Dirección, punto 5); antes iban colapsados y eran opcionales.
  const [notes, setNotes] = useState('');
  // «Tocado»: el aviso rojo de detalles vacíos solo tras salir del campo o
  // intentar guardar. Nada más abrir salía en rojo antes de escribir nada
  // (revisión 2026-09-03); el botón sigue deshabilitado mientras esté vacío.
  const [notesTocado, setNotesTocado] = useState(false);
  const [newPatientOpen, setNewPatientOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  // Agendas de quirófano: lugar de la intervención; externos: cirujano.
  const [lugar, setLugar] = useState('');
  const [cirujano, setCirujano] = useState('');
  // 409 del backend → si es admin, ofrecemos reintentar con force:true.
  const [conflict, setConflict] = useState(false);
  // PACIENTE PROHIBIDO (14/10/2026): por la ficha elegida (el selector trae
  // la ficha entera), la que lee el recuadro clínico o el 409 del servidor.
  const [elegido, setElegido] = useState(null);
  const [fichaLeida, setFichaLeida] = useState(null);
  const [prohibidoServidor, setProhibidoServidor] = useState(false);
  useEffect(() => { setFichaLeida(null); setProhibidoServidor(false); }, [open, patientId]);
  const prohibido = !!patientId && (
    (elegido?.id === patientId && elegido?.prohibido === true)
    || patients.some((p) => p.id === patientId && p.prohibido === true)
    || (fichaLeida?.id === patientId && fichaLeida?.prohibido === true)
    || prohibidoServidor);
  const aviso = useAvisoProhibido(open && prohibido, open ? patientId : null);

  const isQuirofano = agenda?.tipo === 'quirofano';
  const isExterna = !!agenda?.is_externa;
  // Config del DÍA de quirófano (lugar/cirujano son de todo el quirófano):
  // null = sin fijar (la primera cita lo fija), objeto = ya fijado (se hereda).
  const [diaConfig, setDiaConfig] = useState(null);

  // Reset + carga de datos al abrir.
  useEffect(() => {
    if (!open) return;
    setPatientId('');
    setConceptCode('');
    setReserva(false);
    setStart(defaultStart || '');
    setDuracionManual(null);
    setSlots([]);
    setNotes('');
    setNotesTocado(false);
    setSaving(false);
    setLugar('');
    setCirujano('');
    setDiaConfig(null);
    setConflict(false);
    listConceptos()
      .then((cs) => setConceptos(Array.isArray(cs) ? cs : []))
      .catch(() => toast.error('No se pudieron cargar los tipos de cita'));
    api
      .get('/api/patients')
      .then((res) => setPatients(Array.isArray(res.data) ? res.data : []))
      .catch(() => setPatients([]));
    if (agenda?.tipo === 'quirofano' && agenda?.id && defaultDate) {
      getQuirofanoDia(agenda.id, defaultDate)
        .then((cfg) => setDiaConfig(cfg && cfg.lugar ? cfg : null))
        .catch(() => setDiaConfig(null));
    }
  }, [open, defaultStart, agenda?.id, agenda?.tipo, defaultDate]);

  const selectedConcept = useMemo(
    () => conceptos.find((c) => c.code === conceptCode) || null,
    [conceptos, conceptCode],
  );

  /** Duración efectiva de un concepto en esta agenda (override o default). */
  const effectiveMinutes = (c) => minutosConcepto(agenda, c);

  // Duración de la cita: la del tipo elegido en esta agenda, salvo que se
  // haya corregido a mano. Vacía o no válida → el backend resuelve la del
  // concepto, como hasta ahora.
  const minutosDelConcepto = minutosConcepto(agenda, selectedConcept);
  const duration = duracionManual ?? (minutosDelConcepto ? String(minutosDelConcepto) : '');
  const durNum = parseInt(duration, 10);
  const durationMinutes = Number.isFinite(durNum) && durNum > 0 ? durNum : undefined;

  // Huecos libres del día para el concepto elegido y su duración (alimenta
  // el Select de hora).
  useEffect(() => {
    if (!open || !agenda?.id || !conceptCode || !defaultDate) {
      setSlots([]);
      return;
    }
    let cancelled = false;
    setLoadingSlots(true);
    // Con la paciente elegida: sin huecos que la dejen en dos sitios (23/9/2026).
    freeSlots({ agendaId: agenda.id, date: defaultDate, conceptCode, durationMinutes, patientId })
      .then((data) => {
        if (cancelled) return;
        setSlots(Array.isArray(data?.slots) ? data.slots : []);
      })
      .catch(() => {
        if (!cancelled) setSlots([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingSlots(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, agenda?.id, conceptCode, defaultDate, durationMinutes, patientId]);

  // Opciones de hora: los huecos libres + el hueco pulsado aunque no esté listado
  // (p. ej. admin pulsando fuera de horario — el backend validará con force).
  const startOptions = useMemo(() => {
    const opts = slots.map((s) => s.start);
    if (start && !opts.includes(start)) opts.unshift(start);
    if (defaultStart && !opts.includes(defaultStart)) opts.unshift(defaultStart);
    return [...new Set(opts)].sort();
  }, [slots, start, defaultStart]);

  // Solo hay que pedir lugar/cirujano si el día aún no tiene config.
  const needsDiaConfig = isQuirofano && !diaConfig;
  // Externos: el cirujano es OBLIGATORIO y solo de la lista de altas
  // (dirección, 2026-09-08, punto 5). Se pide si el día no está fijado o si
  // está fijado sin un cirujano dado de alta (histórico con la clínica):
  // el backend lo marca con `cirujano_registrado: false` y con la cita se
  // corrige el día entero.
  const needsCirujano = isExterna && (needsDiaConfig || diaConfig?.cirujano_registrado === false);

  const faltanDetalles = !notes.trim();
  const errorDetalles = faltanDetalles && notesTocado;

  const submit = async (force = false) => {
    if (!patientId || !conceptCode || !start || !agenda?.id) return;
    if (aviso.bloquea) {
      aviso.setAvisoAbierto(true);
      return;
    }
    if (faltanDetalles) {
      setNotesTocado(true);
      toast.error('Los detalles de la cita son obligatorios');
      return;
    }
    if (needsDiaConfig && !lugar) {
      toast.error('Indica dónde es el quirófano de ese día');
      return;
    }
    if (needsCirujano && !cirujano.trim()) {
      toast.error('Elige el cirujano de la lista');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        agenda_id: agenda.id,
        patient_id: patientId,
        concept_code: conceptCode,
        date: defaultDate,
        start,
        duration_minutes: durationMinutes,
        notes: notes.trim(),
        // Solo la PRIMERA cita del día fija el quirófano; después se hereda.
        lugar: needsDiaConfig ? lugar : undefined,
        cirujano: needsCirujano ? cirujano.trim() : undefined,
      };
      if (reserva) payload.reserva = true;
      if (force) payload.force = true;
      if (aviso.excepcion) payload.excepcion_prohibido = true;
      const created = await createCita(payload);
      const servicio = selectedConcept?.name || conceptCode;
      toast.success(`Cita creada: ${reserva ? nombreReserva(servicio) : servicio} a las ${start}`);
      if (created?.consents_created?.length) {
        toast.success(`Consentimientos creados: ${created.consents_created.join(', ')}`);
      }
      // Los Detalles nombran otra cirugía que el procedimiento (23/9/2026):
      // el servidor lo dice en `aviso_documentos` y aquí se enseña tal cual.
      if (created?.aviso_documentos) toast.warning(created.aviso_documentos, { duration: 12000 });
      // Mensaje de CONFIRMACIÓN inmediato (distinto del recordatorio del día
      // anterior): se ofrece nada más agendar.
      ofrecerConfirmacionWhatsApp(created?.id);
      onCreated?.();
      onOpenChange(false);
    } catch (e) {
      if (esErrorProhibido(e)) {
        // Sin «Forzar»: esto no se fuerza (`forzable: false`).
        setProhibidoServidor(true);
        toast.error(e?.response?.data?.detail);
      } else if (e?.response?.status === 409) {
        toast.error(e?.response?.data?.detail || 'Ese hueco ya está ocupado');
        // `forzable: false` = la paciente ya está citada en otro sitio a esa
        // hora (23/9/2026): «Forzar» no lo salta, así que no se ofrece.
        if (admin) setConflict(e?.response?.data?.forzable !== false);
      } else {
        toast.error(e?.response?.data?.detail || 'No se pudo crear la cita');
      }
    } finally {
      setSaving(false);
    }
  };

  const canConfirm = !!(patientId && conceptCode && start) && !faltanDetalles && !aviso.bloquea && !saving;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="cita-create-dialog">
          <DialogHeader>
            <DialogTitle>Nueva cita</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Fecha y hora del hueco */}
            <div className="flex items-center justify-between gap-3 rounded-md border border-[#D9E3DD] bg-[rgba(46,110,94,0.06)] px-3 py-2.5">
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">Fecha</div>
                <div className="font-display text-base sm:text-lg font-semibold text-[#13231E] truncate" data-testid="cita-create-date">
                  {longDate(defaultDate)}
                </div>
              </div>
              <div className="shrink-0 w-28">
                <Select value={start} onValueChange={(v) => { setStart(v); setConflict(false); }}>
                  <SelectTrigger className="tabular-nums font-semibold text-[#13231E]" data-testid="cita-create-start">
                    <SelectValue placeholder="Hora" />
                  </SelectTrigger>
                  <SelectContent className="max-h-64">
                    {loadingSlots && (
                      <div className="px-3 py-2 text-xs text-muted-foreground flex items-center gap-2">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Cargando huecos…
                      </div>
                    )}
                    {startOptions.map((h) => (
                      <SelectItem key={h} value={h} className="tabular-nums">
                        {h}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {/* Hora manual: cualquier HH:MM aunque no sea un hueco de la
                    parrilla. El backend valida solape/horario (admin puede
                    forzar con el botón que aparece si hay conflicto). */}
                <input
                  type="time"
                  value={start || ''}
                  onChange={(e) => { if (e.target.value) { setStart(e.target.value); setConflict(false); } }}
                  className="mt-1 w-full h-7 rounded-md border border-[#dbe3ee] bg-white px-2 text-xs tabular-nums text-[#13231E]"
                  title="Teclear otra hora manualmente"
                  data-testid="cita-create-start-manual"
                />
              </div>
            </div>

            {/* Paciente */}
            <div>
              <Label>Paciente</Label>
              <div className="flex gap-2 mt-1.5">
                <div className="flex-1 min-w-0">
                  <PatientCombobox
                    patients={patients}
                    value={patientId}
                    onChange={(id, ficha) => { setPatientId(id); setElegido(ficha || null); setConflict(false); }}
                    dataTestid="cita-create-patient"
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="shrink-0"
                  onClick={() => setNewPatientOpen(true)}
                  data-testid="cita-create-new-patient"
                >
                  <UserPlus className="w-4 h-4 mr-1.5" /> Nuevo
                </Button>
              </div>
              {prohibido && (
                <div className="mt-2">
                  <AvisoProhibidoEnCita
                    puedeExcepcion={aviso.puedeExcepcion}
                    excepcion={aviso.excepcion}
                    onExcepcion={aviso.setExcepcion}
                    testId="cita-create-prohibido"
                  />
                </div>
              )}
            </div>

            {/* Concepto */}
            <div>
              <Label>Tipo de cita</Label>
              <Select
                value={valorTipoCita(conceptCode, reserva)}
                onValueChange={(v) => {
                  const elegido = leerTipoCita(v);
                  setReserva(elegido.reserva);
                  setConflict(false);
                  // «Reserva X» y «X» son el mismo tipo: la duración no cambia.
                  if (elegido.code === conceptCode) return;
                  setConceptCode(elegido.code);
                  setDuracionManual(null);
                }}
              >
                <SelectTrigger className="mt-1.5" data-testid="cita-create-concepto">
                  <SelectValue placeholder="Elegir tipo de cita…" />
                </SelectTrigger>
                <SelectContent>
                  <ConceptoOptions conceptos={conceptos} minutesFor={effectiveMinutes} />
                </SelectContent>
              </Select>
            </div>

            {/* Duración: se rellena sola al elegir el tipo y se puede corregir */}
            <div>
              <Label htmlFor="cita-create-dur">Duración (min)</Label>
              <div className="flex items-center gap-2 mt-1.5">
                <Input
                  id="cita-create-dur"
                  type="number"
                  min={5}
                  step={5}
                  value={duration}
                  onChange={(e) => { setDuracionManual(e.target.value); setConflict(false); }}
                  className="w-24"
                  data-testid="cita-create-duration"
                />
                <span className="text-xs text-muted-foreground">
                  se rellena sola con el tipo de cita; puedes cambiarla
                </span>
              </div>
            </div>

            {/* Quirófano con día ya configurado: se hereda (solo informativo) */}
            {isQuirofano && diaConfig && (
              <div
                className="rounded-md border border-[#D9E3DD] bg-[rgba(46,110,94,0.06)] px-3 py-2 text-sm text-[#13231E]"
                data-testid="cita-create-dia-config"
              >
                Quirófano del día en <span className="font-semibold">{diaConfig.lugar}</span>
                {needsCirujano ? (
                  <span className="text-amber-700 font-medium" data-testid="cita-create-dia-sin-cirujano">
                    {' · Sin cirujano dado de alta'}
                    {diaConfig.cirujano ? ` («${diaConfig.cirujano}» no está en la lista)` : ''}
                    {': elígelo abajo'}
                  </span>
                ) : diaConfig.cirujano ? (
                  <> · Cirujano: <span className="font-semibold">{diaConfig.cirujano}</span></>
                ) : null}
              </div>
            )}

            {/* Primera cita del día: fija el lugar de TODO el quirófano */}
            {needsDiaConfig && (
              <div>
                <Label>Lugar del quirófano (todo el día)</Label>
                <LugarQuirofanoSelect value={lugar} onChange={setLugar} dataTestid="cita-create-lugar"
                  placeholder="¿Dónde es el quirófano ese día?" />
              </div>
            )}

            {/* Externos sin cirujano fijado ese día: obligatorio, de la lista */}
            {needsCirujano && (
              <div>
                <Label>Cirujano de ese quirófano <span className="text-red-600">*</span></Label>
                <CirujanoSelect value={cirujano} onChange={setCirujano} dataTestid="cita-create-cirujano" />
              </div>
            )}

            {/* Lista de espera retirada de la interfaz (2026-08-29). */}

            {/* Detalles: obligatorios y siempre visibles (punto 5) */}
            <div>
              <Label htmlFor="cita-create-notes">Detalles <span className="text-red-600">*</span></Label>
              <Textarea
                id="cita-create-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                onBlur={() => setNotesTocado(true)}
                rows={3}
                placeholder="Ej.: Viene derivada del Dr. López, traer informes previos…"
                className={`mt-1.5 ${errorDetalles ? 'border-red-300' : ''}`}
                aria-invalid={errorDetalles}
                data-testid="cita-create-notes"
              />
              {errorDetalles ? (
                <div className="text-xs text-red-600 mt-1" data-testid="cita-create-notes-error">
                  Los detalles de la cita son obligatorios
                </div>
              ) : faltanDetalles && (
                <div className="text-xs text-muted-foreground mt-1" data-testid="cita-create-notes-ayuda">
                  Obligatorio
                </div>
              )}
            </div>

            {/* Alergias, medicación, ácido hialurónico previo y antecedentes
                médicos de la ficha (solo lectura; punto 9, 2026-09-08) */}
            <InfoClinicaPaciente patientId={patientId} testId="cita-create-info-clinica" onFicha={setFichaLeida} />
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="cita-create-cancel">
              Cancelar
            </Button>
            {conflict && admin && (
              <Button
                type="button"
                variant="outline"
                onClick={() => submit(true)}
                disabled={!canConfirm}
                className="border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800"
                data-testid="cita-create-force"
              >
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Forzar solapamiento
              </Button>
            )}
            <Button
              type="button"
              onClick={() => submit(false)}
              disabled={!canConfirm}
              className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
              data-testid="cita-create-confirm"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Confirmar cita
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DialogoPacienteProhibido
        open={aviso.avisoAbierto}
        onOpenChange={aviso.setAvisoAbierto}
        puedeExcepcion={aviso.puedeExcepcion}
        onExcepcion={() => aviso.setExcepcion(true)}
        testId="cita-create-aviso-prohibido"
      />

      {/* Alta de contacto sin salir de la cita (administración, 2026-09-11). Es el
          formulario de Pacientes, no una copia: así los cuatro datos
          obligatorios del alta se piden aquí igual que en su sección. El
          contacto creado queda elegido para esta cita. */}
      <PatientFormDialog
        open={newPatientOpen}
        onOpenChange={setNewPatientOpen}
        initial={null}
        onSaved={(p) => {
          if (p?.id) {
            setPatients((prev) => [p, ...prev]);
            setPatientId(p.id);
            setConflict(false);
          }
        }}
      />
    </>
  );
}
